import { useEffect, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { firestore } from '../firebase';
import { parseCampusUniversity, type CampusUniversity } from '../utils/campusMaps';

// campusMaps/{universityId} — see scripts/upsertCampusMap.mjs. Loaded once
// per app session (plus a persisted copy so the university picker appears
// instantly on the next launch); a refresh happens in the background.
const STORAGE_KEY = '@tonyte/studizone-campus-maps-v1';

type CampusMapsState = { universities: CampusUniversity[]; loaded: boolean };

let cache: CampusMapsState = { universities: [], loaded: false };
let loadPromise: Promise<void> | null = null;
const listeners = new Set<(state: CampusMapsState) => void>();

function publish(next: CampusMapsState) {
  cache = next;
  listeners.forEach(listener => listener(cache));
}

const sortUniversities = (list: CampusUniversity[]) =>
  [...list].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));

async function load() {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    if (!cache.loaded) {
      try {
        const stored = await AsyncStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored) as Array<{ id: string; data: Record<string, unknown> }>;
          const universities = parsed.map(item => parseCampusUniversity(item.id, item.data)).filter((u): u is CampusUniversity => Boolean(u));
          if (universities.length) publish({ universities: sortUniversities(universities), loaded: false });
        }
      } catch {
        // A corrupt cache just means waiting for Firestore.
      }
    }
    try {
      const snapshot = await getDocs(query(collection(firestore, 'campusMaps'), where('active', '==', true)));
      const raw = snapshot.docs.map(doc => ({ id: doc.id, data: doc.data() as Record<string, unknown> }));
      const universities = raw.map(item => parseCampusUniversity(item.id, item.data)).filter((u): u is CampusUniversity => Boolean(u));
      publish({ universities: sortUniversities(universities), loaded: true });
      AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(raw, (_key, value) => (
        value && typeof value === 'object' && typeof value.toDate === 'function' ? value.toDate().toISOString() : value
      ))).catch(() => {});
    } catch (error) {
      console.warn('[Tonyte][StudiZone] failed to load campus maps', error instanceof Error ? error.message : String(error));
      publish({ ...cache, loaded: true });
    } finally {
      loadPromise = null;
    }
  })();
  return loadPromise;
}

export function useCampusMaps() {
  const [state, setState] = useState(cache);
  useEffect(() => {
    listeners.add(setState);
    setState(cache);
    if (!cache.loaded) void load();
    return () => {
      listeners.delete(setState);
    };
  }, []);
  return state;
}
